<template>
    <div>
        <div>
            <div class="text-h5 q-mb-sm">
                {{ $t('Production Report') }}
            </div>
            <div class="row q-col-gutter-md">
                <date-time-picker
                    v-model="dateTimePicker.start"
                    :label="$t('Start')"
                    class="col-md-4"
                    style="max-width: 250px"
                    dense
                ></date-time-picker>
                <date-time-picker
                    v-model="dateTimePicker.end"
                    :label="$t('End')"
                    class="col-md-4"
                    style="max-width: 250px"
                    dense
                ></date-time-picker>
            </div>
            <div class="q-gutter-sm">
                <div>
                    <b>{{ $t('Show') + ':' }} </b>
                    <q-checkbox
                        @input="handleToggleChange('total')"
                        v-model="table.showTotalCols"
                        :label="$t('Totals')"
                        color="accent"
                    />
                    <q-checkbox
                        @input="handleToggleChange('availability')"
                        v-model="table.showAvailabilityCols"
                        :label="$t('Availabilities')"
                        color="accent"
                    />
                    <q-checkbox
                        @input="handleToggleChange('run_rate')"
                        v-model="table.showRunRateCols"
                        :label="$t('Run rates')"
                        color="accent"
                    />
                </div>
            </div>
        </div>
        <q-table
            :rows="table.data"
            :columns="table.columns"
            :visible-columns="table.visibleColumns"
            :no-data-label="
                $t('No production data found for the given date range.')
            "
        >
            <template v-slot:header="props">
                <q-tr :props="props">
                    <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                        class="bg-secondary text-white"
                    >
                        {{ col.label }}
                    </q-th>
                </q-tr>
            </template>
        </q-table>
    </div>
</template>

<script>
import DateTimePicker from '@components/DateTimePicker.vue';
import { mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';

export default {
    name: 'ProductionReport',
    components: { DateTimePicker },
    props: {
        assetId: {
            type: [Number, String],
            required: true
        }
    },
    watch: {
        /**
         * Handles date time picker initialization and value changes.
         */
        dateTimePicker: {
            deep: true,
            immediate: true,
            async handler(picker) {
                // on initial load, initialize date picker
                if (!(picker.start && picker.end)) {
                    // initialize date picker to be the current week
                    picker.start = new Date(
                        new Date().getTime() - 1000 * 60 * 60 * 24 * 7
                    ).toISOString();
                    picker.end = new Date().toISOString();
                }
                // load table data
                let url = `assets/${this.assetId}/reports/production/?start=${picker.start}&end=${picker.end}`;
                const report = await this.getProductionReport(url);
                this.loadTableData(report);
            }
        }
    },
    data() {
        return {
            dateTimePicker: {
                start: null,
                end: null
            },

            table: {
                data: [],
                columns: [
                    {
                        name: 'name',
                        field: 'name',
                        label: this.$t('Name'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'code',
                        field: 'code',
                        label: this.$t('SKU'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'total_production',
                        field: 'total_production',
                        label: this.$t('Total Production'),
                        align: 'left',
                        sortable: true,
                        format: (val, row) => val.toFixed(0)
                    },
                    {
                        name: 'total_duration',
                        field: 'total_duration',
                        label: this.$t('Total duration (min)'),
                        align: 'left',
                        sortable: true,
                        format: (val, row) => val.toFixed(1)
                    },
                    {
                        name: 'unit_packing',
                        field: 'unit_packing',
                        label: this.$t('Unit packing'),
                        align: 'left',
                        format: (val, row) => val.toFixed(2)
                    },
                    {
                        name: 'downtime_duration',
                        field: 'downtime_duration',
                        label: this.$t('Downtime (min)'),
                        align: 'left',
                        sortable: true,
                        format: (val, row) => val.toFixed(1)
                    },
                    {
                        name: 'uptime_duration',
                        field: 'uptime_duration',
                        label: this.$t('Uptime (min)'),
                        align: 'left',
                        sortable: true,
                        format: (val, row) => val.toFixed(1)
                    },
                    {
                        name: 'downtime_percent',
                        field: 'downtime_percent',
                        label: this.$t('Downtime (%)'),
                        align: 'left',
                        sortable: true,
                        format: (val, row) => `${val.toFixed(1)}%`
                    },
                    {
                        name: 'uptime_percent',
                        field: 'uptime_percent',
                        label: this.$t('Uptime (%)'),
                        align: 'left',
                        sortable: true,
                        format: (val, row) => `${val.toFixed(1)}%`
                    },
                    {
                        name: 'expected_run_rate',
                        field: 'expected_run_rate',
                        label: this.$t('Max run rate (1/min)'),
                        align: 'left',
                        sortable: true,
                        format: (val, row) => val.toFixed(1)
                    },
                    {
                        name: 'actual_run_rate',
                        field: 'actual_run_rate',
                        label: this.$t('Actual run rate (1/min)'),
                        align: 'left',
                        sortable: true,
                        format: (val, row) => val.toFixed(1)
                    },
                    {
                        name: 'run_rate_difference',
                        field: 'run_rate_difference',
                        label: this.$t('Run rate difference (1/min)'),
                        align: 'left',
                        sortable: true,
                        format: (val, row) => val.toFixed(1)
                    }
                ],
                visibleColumns: [
                    'name',
                    'code',
                    'count',
                    'total_duration',
                    'total_production',
                    // 'total_production_unit_packing',
                    'downtime_percent',
                    'uptime_percent',
                    'downtime_duration',
                    'uptime_duration',
                    'expected_run_rate',
                    'actual_run_rate',
                    'run_rate_difference'
                ],
                // checkboxes
                showTotalCols: true,
                showAvailabilityCols: true,
                showRunRateCols: true
            },

            // product selector
            productSelect: {
                selected: [],
                url: `/assets/${this.assetId}/products/`,
                label: this.$t('Select products'),
                dialogTitle: this.$t('Select products'),
                searchLabel: this.$t('Search product'),
                columns: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'code',
                        name: 'code',
                        label: this.$t('SKU'),
                        sortable: true
                    }
                ]
            }
        };
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Handle toggling checkboxes.
         * @param {String} checkbox - Name of checkbox.
         */
        handleToggleChange(checkbox) {
            switch (checkbox) {
                case 'total':
                    this.handleColumnVisibility({
                        checkbox: 'showTotalCols',
                        columns: [
                            'total_duration',
                            'total_production'
                            // 'total_production_unit_packing'
                        ]
                    });
                    break;
                case 'availability':
                    this.handleColumnVisibility({
                        checkbox: 'showAvailabilityCols',
                        columns: [
                            'downtime_percent',
                            'uptime_percent',
                            'downtime_duration',
                            'uptime_duration'
                        ]
                    });
                    break;
                case 'run_rate':
                    this.handleColumnVisibility({
                        checkbox: 'showRunRateCols',
                        columns: [
                            'expected_run_rate',
                            'actual_run_rate',
                            'run_rate_difference'
                        ]
                    });
                    break;
            }
        },
        /**
         * Handle showing/hiding columns when checkbox value changes.
         * Several columns are associated to a single checkbox, so toggling a checkbox should show/unhide the associated columns.
         * @param {String} checkbox - Name of checkbox
         * @param {String[]} columns - Name of columns to show/hide associated with toggling checkbox.
         */
        handleColumnVisibility({ checkbox, columns }) {
            // Note: the checkbox state here is before toggling, so it is negated!
            if (this.table[checkbox]) {
                // join columns
                this.table.visibleColumns =
                    this.table.visibleColumns.concat(columns);
            } else {
                // remove columns
                for (const col of columns) {
                    const index = this.table.visibleColumns.findIndex(
                        c => c === col
                    );
                    this.table.visibleColumns.splice(index, 1);
                }
            }
        },
        /**
         * Get production report from the server.
         * @param {String} url
         */
        async getProductionReport(url) {
            // start the loading animation
            this.beginLoading(`${this.$t('Building report...')}`);
            let data = [];
            await this.$axios
                .get(url)
                .then(response => {
                    data = response.data;
                })
                .catch(error => {
                    console.error('error: ', error);
                })
                .finally(() => {
                    this.stopLoading();
                });
            return data;
        },
        /**
         * Load production data from the server.
         * @param {Object[]} products - Production report rows.
         */
        loadTableData(products) {
            const tableData = [];
            for (const product of products) {
                const row = {
                    name: product['name'],
                    code: product['code'],
                    total_production: product['total_production'],
                    total_duration: product['total_duration'],
                    downtime_percent: product['downtime_percent'],
                    downtime_duration:
                        product['total_duration'] * product['downtime_percent'],
                    uptime_duration:
                        product['total_duration'] -
                        product['total_duration'] * product['downtime_percent'],
                    uptime_percent: 100 - product['downtime_percent']
                };
                // calculate run rates
                const expected_run_rate =
                    product['total_production'] / product['total_duration'];
                // TODO: remove randomizer for actual run rate once actual run rate data is available
                const randomFloat = Math.random() * (0.05 - 0.15) + 0.05;
                const actual_run_rate =
                    expected_run_rate * Math.abs(randomFloat);

                // add run rates
                row['expected_run_rate'] = expected_run_rate;
                row['actual_run_rate'] = actual_run_rate;
                row['run_rate_difference'] =
                    expected_run_rate - actual_run_rate;

                tableData.push(row);
            }

            this.table['data'] = tableData;
        }
    }
};
</script>

<style lang="scss" scoped></style>
